import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tooltip,
  message
} from "antd";
import { deleteSprNumberSpr, fetchSprNumberSprList } from "./spr-number-spr-api";
import SprNumberSpr from "./spr-number-spr";
import {
  VID_TYPES,
  isAllowedVid,
  isExtraPlaceVid,
  isOkIsLech,
  normalizeHtmlColor,
  normalizeRow,
  razmerIsNumber,
  toNumber
} from "./spr-number-spr-utils";
import "./spr-number-sprs.css";

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_VID = 0;

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const filtersFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  const vidRaw = toNumber(src.get("vid"));
  const vid = isAllowedVid(vidRaw) ? vidRaw : DEFAULT_VID;
  const q = src.has("q") ? src.get("q") ?? "" : "";
  return { vid, q };
};

const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  const vid = isAllowedVid(filters?.vid) ? filters.vid : DEFAULT_VID;
  params.set("vid", String(vid));
  const q = typeof filters?.q === "string" ? filters.q.trim() : "";
  if (q) params.set("q", q);
  return params;
};

const listSearchString = (filters) => {
  const s = filtersToSearchParams(filters).toString();
  return s ? `?${s}` : "";
};

/**
 * Справочники номерного фонда (SprNumberSprs). Меню: Справочники → Дополнительные.
 */
const SprNumberSprs = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;

  const urlFilters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const vid = urlFilters.vid;

  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 4) === 1;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState(() => urlFilters.q);
  const [debouncedQ, setDebouncedQ] = useState(() => (urlFilters.q || "").trim());
  const [selectedId, setSelectedId] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);
  const requestIdRef = useRef(0);
  const skipUrlSyncRef = useRef(false);

  const listFilters = useMemo(() => ({ vid, q: debouncedQ }), [vid, debouncedQ]);
  const listFiltersLive = useMemo(() => ({ vid, q: nameFilter }), [vid, nameFilter]);

  useEffect(() => {
    try {
      localStorage.removeItem("planOnline.sprNumberSpr.selectedVid");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((nameFilter || "").trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [nameFilter]);

  useEffect(() => {
    const next = filtersToSearchParams(listFilters);
    if (searchParams.toString() === next.toString()) return;
    skipUrlSyncRef.current = true;
    setSearchParams(next, { replace: true });
  }, [listFilters, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = filtersFromSearchParams(searchParams);
    setNameFilter((prev) => (prev === fromUrl.q ? prev : fromUrl.q));
    setDebouncedQ((prev) => {
      const q = (fromUrl.q || "").trim();
      return prev === q ? prev : q;
    });
  }, [searchParams]);

  const goList = useCallback(
    (opts = {}) => {
      const { replace = false } = opts;
      navigate(`/spr/number-spr${listSearchString(listFiltersLive)}`, { replace });
    },
    [navigate, listFiltersLive]
  );

  const loadItems = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    fetchSprNumberSprList(vid)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const next = (Array.isArray(data) ? data : []).map(normalizeRow).filter((r) => r.id != null);
        setRows(next);
        setCheckedIds((prev) => prev.filter((id) => next.some((r) => r.id === id)));
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось загрузить справочник");
        setRows([]);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [vid]);

  useEffect(() => {
    if (canEdit) loadItems();
  }, [canEdit, loadItems]);

  useEffect(() => {
    if (editId != null) setSelectedId(editId);
  }, [editId]);

  useEffect(() => {
    if (editId == null || loading || !rows.length) return;
    if (!rows.some((r) => r.id === editId)) {
      goList({ replace: true });
    }
  }, [editId, rows, loading, goList]);

  const onSelectVid = useCallback(
    (nextVid) => {
      if (!isAllowedVid(nextVid) || nextVid === vid) return;
      setSelectedId(null);
      setCheckedIds([]);
      setNameFilter("");
      setDebouncedQ("");
      skipUrlSyncRef.current = true;
      setSearchParams(filtersToSearchParams({ vid: nextVid, q: "" }), { replace: true });
    },
    [vid, setSearchParams]
  );

  const filteredRows = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      String(r.name || "")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, nameFilter]);

  const checkedRows = useMemo(
    () => filteredRows.filter((r) => checkedIds.includes(r.id)),
    [filteredRows, checkedIds]
  );
  const hasChecked = checkedRows.length > 0;

  const vidOptions = useMemo(
    () => VID_TYPES.map((t) => ({ value: t.vid, label: t.label })),
    []
  );

  const openEdit = useCallback(
    (record) => {
      if (record?.id == null) return;
      setSelectedId(record.id);
      navigate(`/spr/number-spr/${record.id}${listSearchString(listFiltersLive)}`);
    },
    [navigate, listFiltersLive]
  );

  const openCreate = useCallback(() => {
    if (!canEdit) return;
    navigate(`/spr/number-spr/new${listSearchString(listFiltersLive)}`);
  }, [canEdit, navigate, listFiltersLive]);

  const closeDrawer = useCallback(() => {
    goList();
  }, [goList]);

  const onSaved = useCallback(
    (saved) => {
      loadItems();
      const newId = toNumber(saved?.id ?? saved?.Id);
      if (isNew && newId != null) {
        navigate(`/spr/number-spr/${newId}${listSearchString(listFiltersLive)}`, { replace: true });
      }
    },
    [isNew, loadItems, navigate, listFiltersLive]
  );

  const onDelete = useCallback(() => {
    if (!canEdit || !checkedRows.length) return;
    const ids = checkedRows.map((r) => r.id);
    const many = ids.length > 1;
    Modal.confirm({
      title: many ? `Удалить записи (${ids.length})?` : "Удалить?",
      content: many
        ? checkedRows.map((r) => r.name || `№${r.id}`).join(", ")
        : checkedRows[0].name || `№${ids[0]}`,
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          for (const id of ids) {
            await deleteSprNumberSpr(vid, id);
          }
          message.success("Удалено");
          if (ids.includes(editId)) goList();
          if (ids.includes(selectedId)) setSelectedId(null);
          setCheckedIds([]);
          loadItems();
        } catch (e) {
          message.error(e.message || "Не удалось удалить");
          loadItems();
          throw e;
        }
      }
    });
  }, [canEdit, checkedRows, vid, editId, selectedId, loadItems, goList]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: checkedIds,
      columnWidth: 36,
      onChange: (keys) => setCheckedIds(keys.map(Number).filter(Number.isFinite))
    }),
    [checkedIds]
  );

  const columns = useMemo(() => {
    const okTitle = isOkIsLech(vid) ? "Лечебная" : "Доп.";
    const razmerTitle = razmerIsNumber(vid) ? "Номер" : "Размер";
    const showOk = vid !== 0 && vid !== 5;
    const showColor = isExtraPlaceVid(vid);

    return [
      {
        title: "",
        label: "Статус",
        key: "status",
        width: 44,
        align: "center",
        render: (_, record) => {
          const hidden = Boolean(record.isHide);
          const label = hidden ? "Скрыта" : "Активна";
          return (
            <Tooltip title={label}>
              <span
                className="spr-number-sprs__status-dot"
                data-tone={hidden ? "off" : "on"}
                aria-label={label}
              />
            </Tooltip>
          );
        }
      },
      {
        title: "#",
        dataIndex: "id",
        key: "id",
        width: 70
      },
      {
        title: "Наименование",
        dataIndex: "name",
        key: "name",
        ellipsis: true
      },
      ...(showColor
        ? [
            {
              title: "Цвет",
              dataIndex: "code",
              key: "code",
              width: 90,
              render: (v) => {
                const hex = normalizeHtmlColor(v);
                if (!hex) return "";
                return (
                  <span
                    title={hex}
                    style={{
                      display: "inline-block",
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      background: hex,
                      border: "1px solid rgba(0,0,0,0.15)",
                      verticalAlign: "middle"
                    }}
                  />
                );
              }
            }
          ]
        : []),
      {
        title: razmerTitle,
        dataIndex: "razmer",
        key: "razmer",
        width: 100,
        align: "right",
        render: (v) => (v == null ? "" : String(v))
      },
      ...(showOk
        ? [
            {
              title: okTitle,
              dataIndex: "isOk",
              key: "isOk",
              width: 90,
              align: "center",
              render: (v) => (v ? <CheckOutlined /> : null)
            }
          ]
        : [])
    ];
  }, [vid]);

  if (!canEdit) {
    return (
      <div className="spr-number-sprs">
        <Alert type="warning" showIcon message="Нет права «Настройка номеров»" />
      </div>
    );
  }

  return (
    <div className="spr-number-sprs">
      {error ? (
        <Alert className="spr-number-sprs__error" type="error" showIcon message={error} />
      ) : null}
      <div className="spr-number-sprs__body">
        <div className="spr-number-sprs__list">
          <div className="spr-number-sprs__toolbar">
            <div className="spr-number-sprs__extra-actions">
              <Tooltip title="Создать">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  aria-label="Создать"
                  onClick={openCreate}
                />
              </Tooltip>
              <Tooltip title={hasChecked ? "Удалить" : "Отметьте записи"}>
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!hasChecked}
                    aria-label="Удалить"
                    onClick={onDelete}
                  />
                </span>
              </Tooltip>
            </div>
            <Select
              className="spr-number-sprs__vid-select"
              options={vidOptions}
              value={vid}
              onChange={onSelectVid}
              optionFilterProp="label"
              showSearch
              popupMatchSelectWidth={false}
            />
            <Input
              allowClear
              variant="borderless"
              className="spr-number-sprs__filter"
              placeholder="Поиск по наименованию..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <div className="spr-number-sprs__list-body">
            {loading && !rows.length ? (
              <div className="spr-number-sprs__center">
                <Spin tip="Загрузка..." />
              </div>
            ) : (
              <Table
                className="spr-number-sprs__table"
                size="small"
                rowKey="id"
                columns={columns}
                dataSource={filteredRows}
                loading={loading}
                pagination={false}
                rowSelection={rowSelection}
                onRow={(record) => ({
                  onClick: (e) => {
                    if (e.target.closest(".ant-checkbox-wrapper, .ant-table-selection-column")) {
                      return;
                    }
                    openEdit(record);
                  }
                })}
                rowClassName={(record) =>
                  [
                    "spr-number-sprs__row",
                    record.isHide ? "spr-number-sprs__row--hidden" : "",
                    selectedId != null && record.id === selectedId
                      ? "spr-number-sprs__row--selected"
                      : "",
                    editId != null && record.id === editId
                      ? "spr-number-sprs__row--editing"
                      : ""
                  ]
                    .filter(Boolean)
                    .join(" ")
                }
                scroll={{ x: 640 }}
                locale={{
                  emptyText: nameFilter.trim() ? "Нет записей по фильтру" : "Нет записей"
                }}
              />
            )}
          </div>
        </div>
      </div>
      <SprNumberSpr
        open={drawerOpen}
        vid={vid}
        id={editId}
        isNew={isNew}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
    </div>
  );
};

export default SprNumberSprs;
